import { stringify } from "qs";
import { post } from "./client";
import { type Nominal, type OkResponse, url } from "./common";
import { showroomModels, type ShowroomEntryId } from "./showroom";
import type { SmallTeamId } from "./smallTeam";
import type { SparkId } from "./spark";
import {
  type AnonUserId,
  type IdentityId,
  type ProfileImageData,
  userModels,
  type UserId,
} from "./user";
import type { WorkshopId } from "./workshop";

// ---- IDs ----

export type ShowroomCommentId = Nominal<string, "ShowroomComment">;
export type CommentReactionId = Nominal<string, "CommentReaction">;
export type UnseenCommentId = Nominal<string, "UnseenComment">;

// ---- Author (used by ShowroomComment.author) ----

// Author shape from getComments() — discriminated by `isAnon`.
export type CommentAuthor =
  | {
      isAnon: false;
      identityId: IdentityId;
      id: UserId;
      name: string;
      tag: number;
      isAdmin: boolean;
      profileImageData: ProfileImageData | null;
      lastActionAt: Date;
    }
  | {
      isAnon: true;
      identityId: IdentityId;
      id: AnonUserId;
      name: string | null;
      passedHumanCheck: boolean;
    };

// ---- Entity (with populated author) ----

// Comment as exposed by the API. The bare DB row is augmented with `author` via getComments().
export type ShowroomComment = {
  id: ShowroomCommentId;
  showroomEntryId: ShowroomEntryId;
  identityId: IdentityId;
  content: string;
  lastEditedAt: Date | null;
  parentCommentId: ShowroomCommentId | null;
  reactionCounts: { [emoji: string]: number };
  deletedAt: Date | null;
  deletedBy: UserId | null;
  createdAt: Date;
  author: CommentAuthor;
};

// Returned alongside comments in the small-team showroom-entry response.
export type MyCommentReaction = {
  reaction: string;
  commentId: ShowroomCommentId;
};

// Item in GET /comments/unseen — one row per unseen comment for the requesting user.
export type UnseenComment = {
  commentId: ShowroomCommentId;
  userId: UserId;
  showroomEntryId: ShowroomEntryId;
  workshopId: WorkshopId;
  createdAt: Date;
};

// ---- Models ----

export const commentModels = {
  COMMENTS_UNSEEN: ({
    workshopId,
    showroomEntryId = null,
  }: {
    workshopId: WorkshopId;
    showroomEntryId?: ShowroomEntryId | null;
  }) =>
    url<UnseenComment[]>(
      `/comments/unseen${stringify({ workshopId, showroomEntryId }, { addQueryPrefix: true })}`,
    ),
};

// ---- Mutations ----

// Anonymous posters supply a captcha `token` and display `name` together;
// authenticated callers omit both. The intersection-with-union pattern makes
// the pair atomic — TS rejects passing one without the other.
type AddCommentInput = {
  entryId: ShowroomEntryId;
  content: string;
  parentCommentId?: ShowroomCommentId;
  smallTeamId: SmallTeamId;
  workshopId: WorkshopId;
  sparkId?: SparkId;
} & ({ token: string; name: string } | { token?: never; name?: never });

type UpdateCommentInput = {
  commentId: ShowroomCommentId;
  content: string;
  smallTeamId: SmallTeamId;
};

// Shared by addCommentReaction + removeCommentReaction.
type CommentReactionInput = {
  commentId: ShowroomCommentId;
  reaction: string;
  smallTeamId: SmallTeamId;
};

export const commentMutations = {
  addComment: ({
    entryId,
    content,
    parentCommentId,
    smallTeamId,
    workshopId,
    sparkId,
    token,
    name,
  }: AddCommentInput) =>
    post<OkResponse>(
      `/showroom-entry/${entryId}/add-comment`,
      { content, parentCommentId, token, name },
      [
        showroomModels.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId, true),
        showroomModels.WORKSHOP_SHOWROOM_ENTRIES(workshopId),
        ...(sparkId
          ? [showroomModels.SPARK_SHARED_SHOWROOM_ENTRIES(sparkId)]
          : []),
        ...(token ? [userModels.ME] : []),
      ],
    ),
  updateComment: ({ commentId, content, smallTeamId }: UpdateCommentInput) =>
    post<OkResponse>(`/comments/${commentId}/update`, { content }, [
      showroomModels.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId, true),
    ]),
  // only for admins
  deleteComment: ({
    commentId,
    smallTeamId,
  }: {
    commentId: ShowroomCommentId;
    smallTeamId: SmallTeamId;
  }) =>
    post<OkResponse>(`/comments/${commentId}/delete`, {}, [
      showroomModels.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId, true),
    ]),
  addCommentReaction: ({
    commentId,
    reaction,
    smallTeamId,
  }: CommentReactionInput) =>
    post<OkResponse>(`/comments/${commentId}/add-reaction`, { reaction }, [
      showroomModels.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId, true),
    ]),
  removeCommentReaction: ({
    commentId,
    reaction,
    smallTeamId,
  }: CommentReactionInput) =>
    post<OkResponse>(`/comments/${commentId}/remove-reaction`, { reaction }, [
      showroomModels.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId, true),
    ]),
  seenComment: ({
    commentId,
    workshopId,
  }: {
    commentId: ShowroomCommentId;
    workshopId: WorkshopId;
  }) =>
    post<OkResponse>(`/comments/${commentId}/seen`, {}, [
      commentModels.COMMENTS_UNSEEN({ workshopId }),
    ]),
};
